import * as React from "react";
import { useMemo } from "react";
import styled from "styled-components";
import isEmpty from "lodash/isEmpty";
import EventTypeLabel from "components/events/events/EventTypeLabel";
import PriorityName from "components/events/events/PriorityName";
import usePluginEntities from "hooks/usePluginEntities";
import EventFields from "components/events/events/EventFields";
import useExpandedSections from "components/common/EntityDataTable/hooks/useExpandedSections";
import { Timestamp } from "components/common";
import EventDefinitionLink from "components/events/events/EventDefinitionLink";
import RemediationSteps from "components/events/ReplaySearchSidebar/RemediationSteps";
import ChipsCell from "components/common/ChipsCell";
import useAppendTagFilter from "components/events/useAppendTagFilter";
const EventDefinitionRenderer = ({
  eventDefinitionId,
  meta
}) => {
  var _a, _b, _c;
  const title = (_c = (_b = (_a = meta == null ? void 0 : meta.context) == null ? void 0 : _a.event_definitions) == null ? void 0 : _b[eventDefinitionId]) == null ? void 0 : _c.title;
  return /* @__PURE__ */ React.createElement(EventDefinitionLink, { id: eventDefinitionId, title });
};
export const EventDefinitionTypeRenderer = ({ type }) => {
  const eventDefinitionTypes = usePluginEntities("eventDefinitionTypes");
  const plugin = useMemo(() => {
    if (!type) {
      return null;
    }
    return eventDefinitionTypes.find((edt) => edt.type === type);
  }, [eventDefinitionTypes, type]);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, plugin && plugin.displayName || type);
};
const FieldsRenderer = ({ fields }) => isEmpty(fields) ? /* @__PURE__ */ React.createElement("em", null, "No additional Fields added to this Event.") : /* @__PURE__ */ React.createElement(EventFields, { fields });
const GroupByFieldsRenderer = ({ groupByFields }) => isEmpty(groupByFields) ? /* @__PURE__ */ React.createElement("em", null, "No group-by fields on this Event.") : /* @__PURE__ */ React.createElement(EventFields, { fields: groupByFields });
const StyledDiv = styled.div`
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;
export const MessageRenderer = ({ message, eventId }) => {
  const { toggleSection } = useExpandedSections();
  const toggleExtraSection = () => toggleSection(eventId, "restFieldsExpandedSection");
  return /* @__PURE__ */ React.createElement(StyledDiv, { onClick: toggleExtraSection }, message);
};
const TimeRangeRenderer = ({ eventData }) => eventData.timerange_start && eventData.timerange_end ? /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(Timestamp, { dateTime: new Date(eventData.timerange_start) }), "\u2002\u2014\u2002", /* @__PURE__ */ React.createElement(Timestamp, { dateTime: new Date(eventData.timerange_end) })) : /* @__PURE__ */ React.createElement("em", null, "No time range");
export const TagsRenderer = ({
  tags,
  interactive = true
}) => {
  const onTagClick = useAppendTagFilter();
  if (!interactive) {
    return /* @__PURE__ */ React.createElement(ChipsCell, { items: tags });
  }
  return /* @__PURE__ */ React.createElement(ChipsCell, { items: tags, onItemClick: onTagClick, itemLabel: "tag" });
};
export const eventTypeAttribute = {
  renderCell: (alert) => /* @__PURE__ */ React.createElement(EventTypeLabel, { isAlert: alert }),
  staticWidth: 100
};
export const getGeneralEventAttributeRenderers = () => ({
  message: {
    minWidth: 300,
    width: 0.5,
    renderCell: (message, event) => /* @__PURE__ */ React.createElement(MessageRenderer, { message, eventId: event.id })
  },
  key: {
    renderCell: (key) => /* @__PURE__ */ React.createElement("span", null, key || /* @__PURE__ */ React.createElement("em", null, "No Key set for this Event.")),
    staticWidth: 200
  },
  event_id: {
    staticWidth: 260
  },
  id: {
    staticWidth: 300
  },
  alert: eventTypeAttribute,
  priority: {
    renderCell: (priority) => /* @__PURE__ */ React.createElement(PriorityName, { priority }),
    staticWidth: "matchHeader"
  },
  event_definition_type: {
    renderCell: (type) => /* @__PURE__ */ React.createElement(EventDefinitionTypeRenderer, { type }),
    staticWidth: "matchHeader"
  },
  group_by_fields: {
    renderCell: (groupByFields) => /* @__PURE__ */ React.createElement(GroupByFieldsRenderer, { groupByFields }),
    staticWidth: 400
  },
  tags: {
    renderCell: (tags) => /* @__PURE__ */ React.createElement(TagsRenderer, { tags }),
    width: 0.3
  }
});
const CustomColumnRenderers = {
  attributes: {
    ...getGeneralEventAttributeRenderers(),
    event_definition_id: {
      width: 0.3,
      renderCell: (eventDefinitionId, _, meta) => /* @__PURE__ */ React.createElement(EventDefinitionRenderer, { meta, eventDefinitionId })
    },
    fields: {
      renderCell: (fields) => /* @__PURE__ */ React.createElement(FieldsRenderer, { fields }),
      staticWidth: 400
    },
    remediation_steps: {
      renderCell: (_, event, meta, eventProcedureId) => /* @__PURE__ */ React.createElement(RemediationSteps, { event, eventDefinitionEventProcedureId: eventProcedureId, meta }),
      width: 0.3
    },
    timerange_start: {
      renderCell: (_, event) => /* @__PURE__ */ React.createElement(TimeRangeRenderer, { eventData: event }),
      staticWidth: 320
    }
  }
};
export default CustomColumnRenderers;
